import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";

/**
 * LMS - Part 7: the online exam screen.
 *
 * One question at a time (Previous / Next), a right-side palette (Answered / Not
 * Answered / Marked for Review), a countdown timer and Submit. The paper is drawn
 * once on the server and served back on reload, so a refresh never reshuffles the
 * paper or restarts the clock. When the timer hits zero the exam auto-submits.
 *
 * Best-effort anti-cheat only (right-click / selection off) - like the video
 * screen, a web page cannot truly stop a screenshot.
 */
export default function ExamRunner() {
  const { examId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("studentToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);     // { title, duration_minutes, ... }
  const [attemptId, setAttemptId] = useState(null);
  const [questions, setQuestions] = useState([]); // [{aq_id, question, options, chosen_mcq_id, marked_review}]
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const submittedRef = useRef(false);

  // Point 5: the webcam must be ON during the exam (a proctoring deterrent; it is
  // NOT recorded or uploaded). If the student blocks it, the exam will not start.
  const [cam, setCam] = useState({ ready: false, error: "" });
  const streamRef = useRef(null);
  const videoRef = useRef(null);

  const requestCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = s;
      setCam({ ready: true, error: "" });
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 0);
      return true;
    } catch (e) {
      setCam({ ready: false, error: "Camera access is required to take this exam. Please allow the camera and try again." });
      return false;
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startWithCamera = async () => {
    setLoading(true);
    const ok = await requestCamera();
    if (ok) load();
    else setLoading(false); // show the camera-required screen
  };

  useEffect(() => {
    const stop = (e) => e.preventDefault();
    document.addEventListener("contextmenu", stop);
    document.body.style.userSelect = "none";
    startWithCamera();
    return () => {
      document.removeEventListener("contextmenu", stop);
      document.body.style.userSelect = "";
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getDataAPI(`student/lms/exam/start/${examId}`, token()));
      const d = res.data.data || {};
      if (d.done) {
        alert({ success: "This exam is already submitted." });
        history.replace("/student/lms-exams");
        return;
      }
      setMeta(d.exam);
      setAttemptId(d.attempt_id);
      setQuestions(d.questions || []);
      setRemaining(d.remaining_seconds || 0);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not start the exam." });
      history.replace("/student/lms-exams");
      return;
    }
    setLoading(false);
  };

  // Countdown.
  useEffect(() => {
    if (loading || submittedRef.current) return;
    if (remaining <= 0) {
      doSubmit(true);
      return;
    }
    const t = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, loading]);

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  };

  const persist = async (aq, body) => {
    try {
      await dispatch(postDataAPI("student/lms/exam/answer", { attempt_id: attemptId, aq_id: aq.aq_id, ...body }, token()));
    } catch (e) {
      /* a lost keystroke is non-fatal; the final submit grades what is saved */
    }
  };

  const choose = (mcqId) => {
    const q = questions[idx];
    const next = [...questions];
    next[idx] = { ...q, chosen_mcq_id: mcqId };
    setQuestions(next);
    persist(q, { chosen_mcq_id: mcqId });
  };

  const toggleReview = () => {
    const q = questions[idx];
    const val = q.marked_review ? 0 : 1;
    const next = [...questions];
    next[idx] = { ...q, marked_review: val };
    setQuestions(next);
    persist(q, { marked_review: val });
  };

  const doSubmit = async (auto) => {
    if (submittedRef.current) return;
    if (!auto && !window.confirm("Submit the exam? You cannot change answers after this.")) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await dispatch(postDataAPI("student/lms/exam/submit", { attempt_id: attemptId }, token()));
      alert({ success: auto ? "Time up - exam submitted." : "Exam submitted." });
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not submit." });
    }
    stopCamera();
    history.replace("/student/lms-exams");
  };

  if (loading) return <div style={{ padding: 30 }}>Loading exam…</div>;
  if (!cam.ready) {
    return (
      <div style={{ padding: 40, maxWidth: 520, margin: "40px auto", textAlign: "center" }}>
        <h4>Camera required</h4>
        <p className="text-muted">{cam.error || "This exam needs your camera to be on."}</p>
        <button className="btn btn-primary" onClick={startWithCamera}>Allow camera & start</button>
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-link" onClick={() => history.replace("/student/lms-exams")}>Back</button>
        </div>
      </div>
    );
  }
  if (!questions.length) return <div style={{ padding: 30 }}>No questions available.</div>;

  const q = questions[idx];
  const answeredCount = questions.filter((x) => x.chosen_mcq_id).length;

  // Point 3: when a whole-course (course-wise) paper moves from one subject to
  // the next, announce the subject so the student knows which section they are in
  // and what is coming next.
  const prevQ = idx > 0 ? questions[idx - 1] : null;
  const nextQ = idx < questions.length - 1 ? questions[idx + 1] : null;
  const subjectStart = !!q.subject_name && (!prevQ || prevQ.subject_id !== q.subject_id);
  const nextSubject =
    nextQ && nextQ.subject_name && nextQ.subject_id !== q.subject_id ? nextQ.subject_name : null;

  const palColor = (x) => {
    if (x.marked_review) return "#6f42c1";
    if (x.chosen_mcq_id) return "#28a745";
    return "#adb5bd";
  };

  const low = remaining <= 60;

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
      {/* Point 5: live camera preview (proctoring deterrent, not recorded) */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{
          position: "fixed", bottom: 12, right: 12, width: 150, height: 112,
          background: "#000", border: "2px solid #dc3545", borderRadius: 6, zIndex: 1050, objectFit: "cover",
        }}
      />
      <div style={{ position: "fixed", bottom: 126, right: 12, zIndex: 1050, background: "#dc3545", color: "#fff", fontSize: 11, padding: "1px 6px", borderRadius: 4 }}>
        ● REC
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 style={{ margin: 0 }}>{meta && meta.title}</h5>
        <div
          style={{
            fontSize: 20, fontWeight: 700, padding: "4px 14px", borderRadius: 6,
            background: low ? "#dc3545" : "#0d6efd", color: "#fff",
          }}
        >
          ⏱ {fmt(remaining)}
        </div>
      </div>

      <div className="row">
        {/* Question */}
        <div className="col-md-8">
          <div className="bg-white" style={{ border: "1px solid #ddd", borderRadius: 6, padding: 18, minHeight: 320 }}>
            {q.subject_name ? (
              <div
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: 8, flexWrap: "wrap", marginBottom: 10,
                  padding: "6px 12px", borderRadius: 6,
                  background: subjectStart ? "#14274e" : "#eef1f8",
                  color: subjectStart ? "#fff" : "#14274e",
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                  {subjectStart ? "New section — " : "Subject: "}{q.subject_name}
                </span>
                {nextSubject ? (
                  <span style={{ fontSize: 12, opacity: 0.85 }}>Next subject: {nextSubject}</span>
                ) : null}
              </div>
            ) : null}
            <div className="text-muted mb-2">Question {idx + 1} of {questions.length}</div>
            <div style={{ fontSize: 17, marginBottom: 18, whiteSpace: "pre-wrap" }}>{q.question}</div>

            {q.options.map((o) => (
              <label
                key={o.id}
                style={{
                  display: "block", padding: "10px 12px", marginBottom: 8, borderRadius: 6, cursor: "pointer",
                  border: "1px solid " + (q.chosen_mcq_id === o.id ? "#0d6efd" : "#ccc"),
                  background: q.chosen_mcq_id === o.id ? "#eaf2ff" : "#fff",
                }}
              >
                <input
                  type="radio"
                  name={`q${q.aq_id}`}
                  checked={q.chosen_mcq_id === o.id}
                  onChange={() => choose(o.id)}
                  style={{ marginRight: 10 }}
                />
                {o.text}
              </label>
            ))}
          </div>

          <div className="d-flex justify-content-between mt-3">
            <button className="btn btn-outline-secondary" disabled={idx === 0} onClick={() => setIdx(idx - 1)}>
              ← Previous
            </button>
            <div>
              <button className="btn btn-outline-warning mr-2" onClick={toggleReview}>
                {q.marked_review ? "Unmark Review" : "Mark for Review"}
              </button>
              {q.chosen_mcq_id && (
                <button className="btn btn-outline-danger mr-2" onClick={() => choose(null)}>
                  Clear
                </button>
              )}
              {idx < questions.length - 1 ? (
                <button className="btn btn-primary" onClick={() => setIdx(idx + 1)}>Next →</button>
              ) : (
                <button className="btn btn-success" disabled={submitting} onClick={() => doSubmit(false)}>
                  Submit Exam
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Palette */}
        <div className="col-md-4">
          <div className="bg-white" style={{ border: "1px solid #ddd", borderRadius: 6, padding: 14 }}>
            <div className="mb-2"><b>{answeredCount}</b> of {questions.length} answered</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {questions.map((x, i) => (
                <button
                  key={x.aq_id}
                  onClick={() => setIdx(i)}
                  title={`Question ${i + 1}`}
                  style={{
                    width: 38, height: 38, borderRadius: 6, border: i === idx ? "2px solid #000" : "none",
                    color: "#fff", fontWeight: 600, cursor: "pointer", background: palColor(x),
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <hr />
            <div style={{ fontSize: 13 }}>
              <div><span style={dot("#28a745")} /> Answered</div>
              <div><span style={dot("#adb5bd")} /> Not answered</div>
              <div><span style={dot("#6f42c1")} /> Marked for review</div>
            </div>
            <button className="btn btn-success btn-block mt-3" disabled={submitting} onClick={() => doSubmit(false)}>
              Submit Exam
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const dot = (c) => ({
  display: "inline-block", width: 12, height: 12, borderRadius: 3,
  background: c, marginRight: 8, verticalAlign: "middle",
});
